import {unull} from "./Common";
import type {FormEnvironment} from "@fd/Types";

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;

const DATETIME_PATT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(:(\d{2}))?Z?$/;
const DATE_PATT = /^(\d{4})-(\d{2})-(\d{2})Z?$/;


export interface ParsedDate {
    date: Date | null;
    wholeDay: boolean;
}

/**
 * Parse JSON ISO 8601 datetime to JS Date
 * @param {boolean} end parsed date will be used as a end of interval (this is used only if the date is whole day)
 * @return {{date:Date, wholeDay:boolean}}
 */
export function parseDate(val: string|null|undefined, end = false): ParsedDate {
    const result: ParsedDate = { date: null, wholeDay: false };
    if (!val) result.date = null;
    else if (DATETIME_PATT.test(val)) {
        const hasZ = val.charAt(val.length - 1) === "Z";
        const millis = Date.parse(hasZ ? val : val.concat("Z"));
        let d = new Date(millis);
        if (!hasZ) {
            d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
        }
        result.date = d;
        result.wholeDay = false;
    } else if (DATE_PATT.test(val)) {
        const hasZ = val.charAt(val.length - 1) === "Z";
        const millis = Date.parse(hasZ ? val.substring(0, val.length - 1) : val);
        let d = new Date(millis);
        if (!hasZ) {
            // This was local date(time)
            d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
        }
		if (end) d.setDate(d.getDate() + 1);
        result.date = d;
        result.wholeDay = true;
    } else {
		const date = new Date(val);
		if (!isNaN(date.getTime())) result.date = date;
	}
    return result;
}


/**
 * Format JS Date object to JSON ISO 8601 format (without Z suffix)
 */
export function formatDate(date: Date | null, withTime: boolean, includeSeconds = true): string | null {
    if (date === null) return null;
    const year = date.getFullYear().toString();
    let month = (date.getMonth() + 1).toString();
    let day = date.getDate().toString();
    if (month.length === 1) month = "0" + month;
    if (day.length === 1) day = "0" + day;
    let json = year + "-" + month + "-" + day;
    if (withTime) {
        json = json + "T" + formatTimeOnly(date, includeSeconds);
    }
    return json;
}

/**
 * Format JS Date's time (12:30)
 * @param {Date} date
 * @param {boolean} includeSeconds
 */
export function formatTimeOnly(date:Date, includeSeconds = false):string {
    let hours = date.getHours().toString();
    if (hours.length === 1) hours = "0" + hours;
    let minutes = date.getMinutes().toString();
    if (minutes.length === 1) minutes = "0" + minutes;
    let seconds = date.getSeconds().toString();
    if (seconds.length === 1) seconds = "0" + seconds;
    if (includeSeconds) return hours + ":" + minutes + ":" + seconds;
    else return hours + ":" + minutes;
}


export function sameDay(date1: Date|null|undefined, date2: Date|null|undefined):boolean {
    if (date1 == null || date2 == null) return false;
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

export function sameDayOrBefore(refDay: Date|null|undefined, checkedDay: Date|null|undefined):boolean {
    if (sameDay(refDay, checkedDay)) return true;
    if (refDay == null || checkedDay == null) return false;
    return checkedDay.getTime() < refDay.getTime();
}

export function sameDayOrAfter(refDay: Date|null|undefined, checkedDay: Date|null|undefined):boolean {
    if (sameDay(refDay, checkedDay)) return true;
    if (refDay == null || checkedDay == null) return false;
    return checkedDay.getTime() > refDay.getTime();
}

export function isToday(date: Date|null|undefined):boolean {
    return sameDay(date, new Date());
}

/**
 * Shift given date to the first day of week (monday or sunday)
 */
export function firstDayOfWeek(date: Date): Date {
    const dayShift = (date.getDay() || 7) - 1;
    if (dayShift > 0) {
        return new Date(date.getTime() - dayShift * DAY);
    }
    return date;
}


/**
 * @param {string} existingISO
 * @param {Date} date
 * @return {?string}
 */
export function updateIsoDate(existingISO: string|null|undefined, date: Date|null|undefined): string|null {
    if (!date) {
        return null; // erase date (only time makes no sense)
    }
    let ex;
    const today = {
        date: new Date(),
        wholeDay: false,
    };
    if (existingISO) {
        ex = parseDate(existingISO);
        if (ex.date == null) ex = today;
    } else {
        ex = today;
    }
    if (ex.date) {
        ex.date.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    }
    return formatDate(ex.date, !ex.wholeDay, false);
}

type DateUpdater = (pd: ParsedDate) => void

export function _updateIsoTime(existingISO: string|null|undefined, updater:DateUpdater): string|null {
    const ex = parseDate(existingISO);
    updater(ex);
    return formatDate(ex.date, !ex.wholeDay, false);
}

export function updateIsoTime(existingISO: string|null|undefined, date:Date|null|undefined, withSeconds = true):string|null {
    return _updateIsoTime(existingISO, (ex) => {
        if (date) {
            if (!ex.date) ex.date = new Date(); // there is no date, we use current day
            ex.date.setHours(date.getHours());
            ex.date.setMinutes(date.getMinutes());
            ex.date.setSeconds(withSeconds ? date.getSeconds() : 0);
            ex.date.setMilliseconds(0); // no interest
            ex.wholeDay = false;
        } else {
            ex.wholeDay = true;
        }
    });
}

export function updateIsoHour(existingISO: string|null|undefined, hour:number|null|undefined):string|null {
    return _updateIsoTime(existingISO, (ex) => {
        if (typeof hour === "number") {
            if (!ex.date) {
                ex.date = new Date();
                ex.date.setMinutes(0);
            }
            ex.date.setHours(hour);
            ex.wholeDay = false;
        } else {
            ex.wholeDay = true;
        }
    });
}

export function updateIsoMin(existingISO: string|null|undefined, min:number|null|undefined):string|null {
    return _updateIsoTime(existingISO, (ex) => {
        if (typeof min === "number") {
            if (!ex.date) {
                ex.date = new Date();
                ex.date.setHours(0);
            }
            ex.date.setMinutes(min);
            ex.wholeDay = false;
        } else {
            ex.wholeDay = true;
        }
    });
}

/**
 * This type is exported by Lang.patternToJson()
 * and consumed by date time editors
 */
export interface DatePattern {
    /** Group number of the day (group of pattern) */
    d: number;
    /** Group number of the month (group of pattern) */
    M: number;
    /** Group number of the year (group of pattern) */
    y: number;
    /** Temporal pattern (can be used as placeholder) */
    pattern: string;

    /** Regex pattern (can be used for matching) */
    regex: RegExp;
}

/**
 * This type is exported by Lang.patternToJson()
 * and consumed by date time editors
 */
export interface DateTimePattern extends DatePattern {
    /** Group number of the hour (group of pattern) */
    H: number;
    /** Group number of the minute (group of pattern) */
    m: number;
}

/**
 * Use this function to shift Date by specified number of days.
 * It returns copy of original date (which remains unchanged)
 */
export function newShifted(d:Date, days = 1):Date {
	let newDate = new Date(d);
	if (days !== 0) newDate.setDate(newDate.getDate() + days); // Shift date, preserves time
	return newDate;
}

/**
 * Use this function to shift Date by specified number of days.
 * It returns the same but modified date.
 */
export function shiftDate(d:Date, days = 1):Date {
	if (days !== 0) d.setDate(d.getDate() + days); // Shift date, preserves time
	return d;
}

export function findPrevMonday(d:Date):Date {
	const wd = d.getDay();
	let shift = 0;
	switch (wd) {
		case 0: shift = -6; break; /* sun */
		case 1: shift = -7; break; /* mon */
		case 2: shift = -1; break; /* tue */
		case 3: shift = -2; break; /* wed */
		case 4: shift = -3; break; /* thu */
		case 5: shift = -4; break; /* fri */
		case 6: shift = -5; break; /* sat */
	}
	let newDate = new Date(d);
	newDate.setDate(newDate.getDate() + shift); // Shift date, preserves time
	return newDate;
}

export function findNextMonday(d:Date):Date {
	const wd = d.getDay();
	let shift = 0;
	switch (wd) {
		case 0: shift = 1; break; /* sun */
		case 1: shift = 7; break; /* mon */
		case 2: shift = 6; break; /* tue */
		case 3: shift = 5; break; /* wed */
		case 4: shift = 4; break; /* thu */
		case 5: shift = 3; break; /* fri */
		case 6: shift = 2; break; /* sat */
	}
	let newDate = new Date(d);
	newDate.setDate(newDate.getDate() + shift); // Shift date, preserves time
	return newDate;
}

export function mondayDate(d:Date, usePrev = true):Date {
	if (d.getDay() === 1) return d;	// already monday
	return usePrev ? findPrevMonday(d) : findNextMonday(d);
}

/**
 * Return date shifted by n days from today
 * @param shift offset from today 0 = today, 1 = tomorrow, -1 = yesterday
 */
function day(shift: number):Date {
	const d = new Date();
	d.setHours(12, 0, 0, 0);	// noon today
	d.setDate(d.getDate() + shift);
	return d;
}

export function smartDate(parsed: ParsedDate, env: FormEnvironment) {
	if (parsed == null || parsed.date == null) return "-";
	const date = parsed.date;
	const wholeDay = parsed.wholeDay;

	const diffMins: number = Math.floor((date.getTime() - Date.now()) / 60000);	// positive means future

	if (sameDay(date, new Date())) {
		if (wholeDay) return env.msg("sdate.today");
		if (diffMins == 0) return env.msg("sdate.now");
		if (diffMins < 0) {
			if (diffMins == -1) return env.msg("sdate.minM1");
			if (diffMins == -2) return env.msg("sdate.minM2");
			if (diffMins == -3) return env.msg("sdate.minM3");
			if (diffMins == -4) return env.msg("sdate.minM4");
			if (diffMins > -60) return env.msg("sdate.minM", -1 * diffMins);
			const hours = Math.floor(diffMins / -60);
			if (hours == 1) return env.msg("sdate.hourM1");
			return env.msg("sdate.hourM", hours);
		} else {
			if (diffMins == 1) return env.msg("sdate.minP1");
			if (diffMins == 2) return env.msg("sdate.minP2");
			if (diffMins == 3) return env.msg("sdate.minP3");
			if (diffMins == 4) return env.msg("sdate.minP4");
			if (diffMins < 60) return env.msg("sdate.minP", diffMins);
			const hours = Math.floor(diffMins / 60);
			if (hours == 1) return env.msg("sdate.hourP1");
			return env.msg("sdate.hourP", hours);
		}
	}
	if (diffMins < 0) {
		if (sameDay(date, day(-1)))
			return env.msg("sdate.dayM1");
		if (sameDay(date, day(-2)))
			return env.msg("sdate.dayM2");
		if (sameDay(date, day(-3)))
			return env.msg("sdate.dayM3");
		if (sameDay(date, day(-4)))
			return env.msg("sdate.dayM4");
		if (sameDay(date, day(-7)))
			return env.msg("sdate.weekAgo");
		return env.msg("sdate.dayM", Math.floor(diffMins / -1440));
	} else {
		if (sameDay(date, day(1)))
			return env.msg("sdate.dayP1");
		if (sameDay(date, day(2)))
			return env.msg("sdate.dayP2");
		if (sameDay(date, day(3)))
			return env.msg("sdate.dayP3");
		if (sameDay(date, day(4)))
			return env.msg("sdate.dayP4");
		if (sameDay(date, day(7)))
			return env.msg("sdate.nextWeek");
		return env.msg("sdate.dayP", Math.floor(diffMins / 1440));
	}
}

/**
 * Format for humans (input fields)
 */
export function format(parsed:ParsedDate|undefined|null, env:FormEnvironment):string {
	return parsed == null || parsed.date == null ? "" : patternFormat(parsed.date, parsed.wholeDay ? env.formats.date.pattern : env.formats.datetime.pattern);
}

/**
 * Format Date based on pattern
 */
function patternFormat(date:Date|null, pattern:string):string {
	if (date == null) return '';
	const year = date.getFullYear().toString();
	let month = (date.getMonth()+1).toString();
	let day = date.getDate().toString();
	let hours = date.getHours().toString();
	let minutes = date.getMinutes().toString();
	let zmonth = month.length === 1 ? '0'+month : month;
	let zday = day.length === 1 ? '0'+day : day;
	let zhours = hours.length === 1 ? '0'+hours : hours;
	let zminutes = minutes.length === 1 ? '0'+minutes : minutes;
	return pattern.replace('yyyy', year)
		.replace('MM', zmonth)
		.replace('dd', zday)
		.replace('HH', zhours)
		.replace('mm', zminutes)
		.replace('M', month)
		.replace('d', day)
		.replace('H', hours)
		.replace('m', minutes);
}
